import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

interface PlayerState {
  isPlaying: boolean;
  currentSeconds: number;
  totalSeconds: number;
  activeTranscriptId: string | null;
  activeDocumentPage: number;
  totalDocumentPages: number;
  /** Set by tile clicks to trigger muted autoplay once the new video loads. */
  pendingAutoplay: boolean;
  /** Set by search result clicks to seek to the best clip start once the new video loads. */
  pendingSeekSeconds: number | null;
  /** Hard playback-stop boundary (seconds) for the current best_match clip, if any. */
  activeClipEndSeconds: number | null;
}

const initialState: PlayerState = {
  isPlaying: false,
  currentSeconds: 0,
  totalSeconds: 33,         // Real Value of AI (~0:33)
  activeTranscriptId: 'at-1',
  activeDocumentPage: 1,
  totalDocumentPages: 3,
  pendingAutoplay: false,
  pendingSeekSeconds: null,
  activeClipEndSeconds: null,
};

const playerSlice = createSlice({
  name: 'player',
  initialState,
  reducers: {
    togglePlay(state) {
      state.isPlaying = !state.isPlaying;
    },
    setPlaying(state, action: PayloadAction<boolean>) {
      state.isPlaying = action.payload;
    },
    seekToSeconds(state, action: PayloadAction<number>) {
      state.currentSeconds = action.payload;
      state.isPlaying = true;
    },
    /** Called by the video element's timeupdate — only syncs time, never changes isPlaying */
    updateCurrentTime(state, action: PayloadAction<number>) {
      state.currentSeconds = action.payload;
    },
    setTotalSeconds(state, action: PayloadAction<number>) {
      state.totalSeconds = action.payload;
    },
    setActiveTranscript(state, action: PayloadAction<string>) {
      state.activeTranscriptId = action.payload;
    },
    jumpToPage(state, action: PayloadAction<number>) {
      state.activeDocumentPage = action.payload;
    },
    /** Tile click: request muted autoplay once the new video metadata loads. */
    requestAutoplay(state) {
      state.pendingAutoplay = true;
    },
    /** VideoPlayer clears this after honoring the autoplay request. */
    clearAutoplay(state) {
      state.pendingAutoplay = false;
    },
    /** Search result click: seek to this position (seconds) once the new video metadata loads. */
    requestSeek(state, action: PayloadAction<number>) {
      state.pendingSeekSeconds = action.payload;
    },
    /** VideoPlayer clears this after honoring the seek request. */
    clearSeek(state) {
      state.pendingSeekSeconds = null;
    },
    /** Search result click on a clip-type best_match: set/clear the hard playback-stop boundary. */
    setClipEndBoundary(state, action: PayloadAction<number | null>) {
      state.activeClipEndSeconds = action.payload;
    },
  },
});

export const {
  togglePlay,
  setPlaying,
  seekToSeconds,
  updateCurrentTime,
  setTotalSeconds,
  setActiveTranscript,
  jumpToPage,
  requestAutoplay,
  clearAutoplay,
  requestSeek,
  clearSeek,
  setClipEndBoundary,
} = playerSlice.actions;
export default playerSlice.reducer;
